import { useEffect, useState } from "react";
import { Alert, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel } from "@mui/material";
import { Button, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { SecretKeyField } from "./SecretKeyField";

type SecretRevealDialogProps = {
    open: boolean;
    secretKey: string | null;
    title: string;
    description: string;
    onAcknowledge: () => void;
};

export const SecretRevealDialog = ({ open, secretKey, title, description, onAcknowledge }: SecretRevealDialogProps) => {
    const { t } = useTranslation();
    const [hasCopied, setHasCopied] = useState(false);
    const [hasConfirmed, setHasConfirmed] = useState(false);

    useEffect(() => {
        if (open) {
            setHasCopied(false);
            setHasConfirmed(false);
        }
    }, [open, secretKey]);

    if (!secretKey) {
        return null;
    }

    const canAcknowledge = hasCopied || hasConfirmed;

    return (
        <Dialog open={open} maxWidth="sm" fullWidth aria-labelledby="secret-reveal-dialog-title">
            <DialogTitle id="secret-reveal-dialog-title">{title}</DialogTitle>
            <DialogContent sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(2) })}>
                <Typography variant="body-regular">{description}</Typography>
                <Alert severity="warning">
                    {t(
                        "applications.secretRevealOnlyOnce",
                        "This is the only time this secret is shown. Copy it now — if you lose it, you'll need to regenerate a new one.",
                    )}
                </Alert>
                <SecretKeyField secretKey={secretKey} onCopy={() => setHasCopied(true)} />
                <FormControlLabel
                    control={
                        <Checkbox checked={hasConfirmed} onChange={(event) => setHasConfirmed(event.target.checked)} />
                    }
                    label={t("applications.secretRevealConfirmCheckbox", "I've copied and securely stored this secret")}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onAcknowledge} variant="contained" disabled={!canAcknowledge}>
                    {t("applications.secretRevealDone", "Done")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
